<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { NEmpty, NSelect, NSpin } from 'naive-ui'
import { useProductOwnerRunsStore } from '../stores/product_owner_runs'
import { useProductOwnerBacklogStore } from '../stores/product_owner_backlog'
import ProductOwnerActivityHeatmap from './ProductOwnerActivityHeatmap.vue'
import ProductOwnerActivityRepoHeatmap from './ProductOwnerActivityRepoHeatmap.vue'
import ProductOwnerActivityBacklogChart from './ProductOwnerActivityBacklogChart.vue'

// Activity tab (#1036 droplet-wide daily heatmap; #1210 adds a per-repo
// hourly drill-down below it) — split out of AdminProductOwnerRuns.vue into
// its own self-contained component, same reasoning as ProductOwnerReviewTab
// (that file was already near the 300-line limit, and #1210's repo/window
// selectors are exactly the kind of tab-owned state that pattern exists
// for). Unlike Review, this tab owns TWO independently-loading sections:
// the unchanged all-repo daily heatmap, and the new per-repo hourly one —
// each gets its own loading/error/empty handling since one can be ready
// while the other is still fetching.
const store = useProductOwnerRunsStore()
const backlogStore = useProductOwnerBacklogStore()

const WINDOW_OPTIONS = [
  { label: 'Last 24 hours', value: 24 },
  { label: 'Last 3 days', value: 72 },
  { label: 'Last 7 days', value: 168 },
]
// The burndown (#1738) is daily, so it takes Review's 7/30/90-day windows
// rather than the hourly section's; it shares the repo selector above.
const BACKLOG_WINDOW_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
]
const windowHours = ref(168)
const backlogWindowDays = ref(30)
// Sentinel for the combined view (#1963); the API takes a blank repo as "every repo".
const ALL_REPOS = '__all__'
const selectedRepo = ref(ALL_REPOS)
const apiRepo = computed(() => (selectedRepo.value === ALL_REPOS ? '' : selectedRepo.value))

// Distinct repos actually present in the run history — same source
// AdminProductOwnerRuns.vue's own repo filter already uses, so a repo only
// needs to show up in `store.runs` once for both filters to know about it.
const repoNames = computed(() => Array.from(new Set(store.runs.map((r) => r.repo))).toSorted())
const repoOptions = computed(() => [
  { label: 'All repos', value: ALL_REPOS },
  ...repoNames.value.map((repo) => ({ label: repo, value: repo })),
])

async function loadHourly(): Promise<void> {
  try {
    await store.fetchActivityHourly(apiRepo.value, windowHours.value)
  } catch {
    /* activityHourlyLoadError surfaces in the template */
  }
}
watch([selectedRepo, windowHours], loadHourly, { immediate: true })

async function loadBacklog(): Promise<void> {
  try {
    await backlogStore.fetchBacklog(apiRepo.value, backlogWindowDays.value)
  } catch {
    /* backlogLoadError surfaces in the template */
  }
}
watch([selectedRepo, backlogWindowDays], loadBacklog, { immediate: true })

onMounted(async () => {
  try {
    await store.fetchActivity()
  } catch {
    /* activityLoadError surfaces in the template */
  }
})
</script>

<template>
  <div class="flex flex-col gap-6" data-testid="product-owner-activity-tab">
    <div v-if="store.activityLoading && !store.activityLoaded" class="flex items-center justify-center py-12">
      <NSpin />
    </div>

    <div
      v-else-if="store.activityLoadError"
      class="text-red-300 text-sm"
      data-testid="product-owner-activity-load-error"
    >
      Failed to load activity: {{ store.activityLoadError }}
    </div>

    <NEmpty
      v-else-if="store.activityDays.length === 0"
      description="No activity data yet."
      class="my-12"
      data-testid="product-owner-activity-empty"
    />

    <ProductOwnerActivityHeatmap v-else :days="store.activityDays" />

    <div class="flex flex-col gap-2">
      <h3 class="text-sm font-semibold text-white/80 m-0">Per repo</h3>

      <div class="w-full grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="product-owner-activity-hourly-filters">
        <label class="flex flex-col gap-1 text-xs text-white/60">
          Repo
          <NSelect
            v-model:value="selectedRepo"
            :options="repoOptions"
            data-testid="product-owner-activity-hourly-repo"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-white/60">
          Window
          <NSelect
            v-model:value="windowHours"
            :options="WINDOW_OPTIONS"
            data-testid="product-owner-activity-hourly-window"
          />
        </label>
      </div>

      <p
        v-if="repoNames.length === 0"
        class="text-xs text-white/40 m-0"
        data-testid="product-owner-activity-hourly-no-repos"
      >
        No repos yet.
      </p>

      <div
        v-else-if="store.activityHourlyLoading && !store.activityHourlyLoaded"
        class="flex items-center justify-center py-8"
      >
        <NSpin />
      </div>

      <div
        v-else-if="store.activityHourlyLoadError"
        class="text-red-300 text-sm"
        data-testid="product-owner-activity-hourly-load-error"
      >
        Failed to load hourly activity: {{ store.activityHourlyLoadError }}
      </div>

      <NEmpty
        v-else-if="store.activityHourlyHours.length === 0"
        description="No runs in this window."
        class="my-8"
        data-testid="product-owner-activity-hourly-empty"
      />

      <ProductOwnerActivityRepoHeatmap v-else :hours="store.activityHourlyHours" :window-hours="windowHours" />
    </div>

    <div v-if="repoNames.length > 0" class="flex flex-col gap-2" data-testid="product-owner-activity-backlog">
      <div class="flex flex-wrap items-end justify-between gap-2">
        <h3 class="text-sm font-semibold text-white/80 m-0">Backlog burndown</h3>
        <label class="flex flex-col gap-1 text-xs text-white/60 w-40">
          Window
          <NSelect
            v-model:value="backlogWindowDays"
            :options="BACKLOG_WINDOW_OPTIONS"
            size="small"
            data-testid="product-owner-activity-backlog-window"
          />
        </label>
      </div>

      <div
        v-if="backlogStore.backlogLoading && !backlogStore.backlogLoaded"
        class="flex items-center justify-center py-8"
      >
        <NSpin />
      </div>

      <div
        v-else-if="backlogStore.backlogLoadError"
        class="text-red-300 text-sm"
        data-testid="product-owner-activity-backlog-load-error"
      >
        Failed to load backlog history: {{ backlogStore.backlogLoadError }}
      </div>

      <NEmpty
        v-else-if="backlogStore.backlogDays.length === 0"
        description="No backlog snapshots in this window."
        class="my-8"
        data-testid="product-owner-activity-backlog-empty"
      />

      <ProductOwnerActivityBacklogChart v-else :days="backlogStore.backlogDays" :window-days="backlogWindowDays" />
    </div>
  </div>
</template>
